import { memo, ReactNode, useCallback, useEffect, useMemo, useState } from "react"

// ** Third Party Components
import { useLocation } from "react-router-dom"
import {
  Chart1,
  ClipboardText,
  DollarSquare,
  FolderCloud,
  Home2,
  Profile2User,
  TaskSquare,
} from "iconsax-reactjs"

// ** Routes
import {
  ANALYTICS_PAGE_ROUTE,
  FILES_FOLDER_PAGE_ROUTE,
  FINANCE_PAGE_ROUTE,
  MY_TASKS_PAGE_ROUTE,
  NEW_DASHBOARD_PAGE_ROUTE,
  PINNED_PROJECT_PAGE_ROUTE,
  PROJECT_GROUP_PAGE_ROUTE,
  PROJECT_PAGE_ROUTE,
  USER_PAGE_ROUTE,
} from "@pma/utils/page-routes"

// ** Helpers
import { cn } from "@pma/utils/helpers"

// ** Store
import { useCommonStore } from "@pma/stores/common-store"

// ** Custom Hooks
import { usePermissions } from "@pma/hooks/use-permissions"

// ** Service
import {
  getProjectListService,
  getSelectedProjectGroupsAndProjectListService,
} from "@pma/services/project"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

// ** Custom Components
import NavBarItems from "./Navbar"
import NavbarHeader from "./NavbarHeader"
import NavbarBottom from "./NavbarBottom"
import { getFolderListService } from "@pma/services/files"
import { FolderTreeResult } from "@pma/types"

type Project = {
  id: number
  title: string
  group_id: number
}

type TreeNode =
  | {
      title: string
      icon?: ReactNode
      children: TreeNode[]
      group_id?: number
      isExpandable?: boolean
      disabled?: boolean
      externalLink?: boolean
      breadcrumbs?: any[]
      openInNewTab?: boolean
    }
  | {
      title: string
      path: string
      icon?: ReactNode
      disabled?: boolean
      breadcrumbs?: any[]
      externalLink?: boolean
      openInNewTab?: boolean
    }

interface SideBarProps {
  isOpen: boolean
  onClose: () => void
  className: string
}

type Group = {
  id: number
  name: string
  _sub_groups: Group[]
  _projects: Project[]
}

type Breadcrumb = {
  title: string
  path: string
}

type ProjectGroup = {
  title: string
  path: string
  breadcrumbs: Breadcrumb[]
  children?: ProjectGroup[]
}

const Sidebar = ({ isOpen = true, onClose, className }: SideBarProps) => {
  const location = useLocation()
  const { translate, locale } = useTranslator()
  const {
    projectUpdate,
    isRefetchFolders,
    setProjectUpdateAction,
    setIsRefetchFoldersAction,
    setProjectGroupListListAction,
    setHeaderBreadcrumbsListAction,
  } = useCommonStore()
  const { currentOrg, organization_id } = usePermissions()

  const isHebrew = locale === "he"
  const userRole = currentOrg?.role

  const navItems = useMemo(
    () =>
      [
        {
          icon: Home2,
          type: "home",
          title: "Home",
          path: NEW_DASHBOARD_PAGE_ROUTE,
          breadcrumbs: [{ title: "Home", path: NEW_DASHBOARD_PAGE_ROUTE }],
        },
        {
          icon: ClipboardText,
          type: "project",
          title: "Project",
          toggles: "project",
          path: PROJECT_PAGE_ROUTE,
          breadcrumbs: [{ title: "Project" }],
        },
        {
          icon: TaskSquare,
          type: "my-tasks",
          title: "My Tasks",
          path: MY_TASKS_PAGE_ROUTE,
          breadcrumbs: [{ title: "My Tasks", path: MY_TASKS_PAGE_ROUTE }],
        },
        {
          icon: FolderCloud,
          title: "Files",
          toggles: "files",
          path: FILES_FOLDER_PAGE_ROUTE,
          breadcrumbs: [{ title: "Files", path: FILES_FOLDER_PAGE_ROUTE }],
        },
        {
          icon: Profile2User,
          title: "Users",
          path: USER_PAGE_ROUTE,
          breadcrumbs: [{ title: "Users", path: USER_PAGE_ROUTE }],
        },
        {
          icon: Chart1,
          title: "Analytics",
          path: ANALYTICS_PAGE_ROUTE,
          breadcrumbs: [{ title: "Analytics", path: ANALYTICS_PAGE_ROUTE }],
        },
        {
          icon: DollarSquare,
          title: "Financial",
          path: FINANCE_PAGE_ROUTE,
          breadcrumbs: [{ title: "Financial", path: FINANCE_PAGE_ROUTE }],
        },
        {
          title: "Pinned Projects",
          group_id: 2,
          path: PINNED_PROJECT_PAGE_ROUTE,
          children: [],
          defaultCollapsed: true,
          breadcrumbs: [{ title: "Pinned Projects", path: PINNED_PROJECT_PAGE_ROUTE }],
        },
        {
          title: "Projects",
          group_id: 0,
          path: PROJECT_PAGE_ROUTE,
          children: [],
          menuOf: "project",
          breadcrumbs: [{ title: "All Projects", path: PROJECT_PAGE_ROUTE }],
        },
        {
          title: "Files",
          group_id: 1,
          path: FILES_FOLDER_PAGE_ROUTE,
          children: [],
          menuOf: "files",
          breadcrumbs: [{ title: "Files", path: FILES_FOLDER_PAGE_ROUTE }],
        },
      ].filter(Boolean),
    [userRole],
  )

  const [allNavbarItems, setAllNavbarItems] = useState<any[]>(navItems)
  const [loadingGroupId, setLoadingGroupId] = useState<number | null>(null)
  // Single source of truth for which top-level menu is expanded. `null` means
  // neither Project nor Files dropdown is visible.
  const [openMenu, setOpenMenu] = useState<"project" | "files" | null>(null)

  // Track if data has been loaded for each section
  const [projectsLoaded, setProjectsLoaded] = useState(false)
  const [pinnedProjectsLoaded, setPinnedProjectsLoaded] = useState(false)
  const [filesLoaded, setFilesLoaded] = useState(false)

  const handleSetBreadcrumbs = (newNavbarItems: any[]) => {
    const breadcrumbsData = JSON.parse(localStorage.getItem("breadcrumbs") || "[]")
    const findBreadcrumb = findProjectByPath(newNavbarItems, location.pathname)

    if (findBreadcrumb.node) {
      if (breadcrumbsData.length) {
        const isMatch = breadcrumbsData[breadcrumbsData.length - 1].path == location.pathname
        if (!isMatch) {
          localStorage.setItem("breadcrumbs", JSON.stringify(findBreadcrumb.node.breadcrumbs))
          setHeaderBreadcrumbsListAction(findBreadcrumb.node.breadcrumbs)
        }
      } else {
        localStorage.setItem("breadcrumbs", JSON.stringify(findBreadcrumb.node.breadcrumbs))
        setHeaderBreadcrumbsListAction(findBreadcrumb.node.breadcrumbs)
      }
    }
  }

  function flattenGroups(groups: any) {
    let result: any = []

    function traverse(groups: any, parentGroupId: number | null = null) {
      groups.forEach((group: any) => {
        result.push({
          id: group.id,
          name: group.name,
          parent_group_id: parentGroupId,
        })

        if (group._sub_groups && group._sub_groups.length > 0) {
          traverse(group._sub_groups, group.id)
          delete group._sub_groups // Remove _sub_groups from the current group
        }
      })
    }

    traverse(groups)
    return result
  }

  const handleGetSelectedProjectGroup = useCallback(async () => {
    // Skip if already loaded and not a forced update
    if (projectsLoaded && !projectUpdate) {
      return
    }

    setLoadingGroupId(0) // 0 = Projects group_id

    await getSelectedProjectGroupsAndProjectListService({ organization_id, show_deleted: false })
      .then((response) => {
        if (Object.keys(response).length > 0) {
          const projectNavbar: any = []
          Object.keys(response).map((key) => {
            if (key == "project_groups") {
              response[key].map((group: any) => {
                const data = buildNode(group, [{ title: "Project", path: PROJECT_PAGE_ROUTE }])
                projectNavbar.push(data)
              })
            } else {
              response[key].map((project: any) => {
                projectNavbar.push({
                  title: project?.title || "",
                  path: `${PROJECT_PAGE_ROUTE}/${project?.id}`,
                  breadcrumbs: [
                    { title: "Project", path: PROJECT_PAGE_ROUTE },
                    { title: project?.title || "", path: `${PROJECT_PAGE_ROUTE}/${project?.id}` },
                  ],
                })
              })
            }
          })

          const groupList = flattenGroups(response?.project_groups)
          setProjectGroupListListAction(groupList)

          setAllNavbarItems((prev) =>
            prev.map((nav) => {
              if (nav.title == "Projects")
                return { ...nav, path: PROJECT_PAGE_ROUTE, children: projectNavbar }
              return nav
            }),
          )

          setProjectsLoaded(true)
          setLoadingGroupId(null)
        }
      })
      .catch((error) => {
        setLoadingGroupId(null)
        toast({
          title: translate("Error"),
          description: translate(error?.message),
          variant: "error",
        })
      })
  }, [organization_id, projectsLoaded, projectUpdate])

  const handleGetFolderList = useCallback(
    async (forceRefetch: boolean = false) => {
      // Skip if already loaded and not forcing refetch
      if (filesLoaded && !forceRefetch) {
        return
      }

      setLoadingGroupId(1) // 1 = Files group_id

      try {
        const data = await getFolderListService({ organization_id })

        if (data) {
          const newArray = buildFolderHierarchy(data.tree, data.folders)
          const tree = buildFolderTreeWithBreadcrumbs(newArray)

          setAllNavbarItems((prev) =>
            prev.map((nav) => {
              if (nav.title == "Files" && nav.group_id === 1) return { ...nav, children: tree }
              return nav
            }),
          )

          setFilesLoaded(true)
          setLoadingGroupId(null)
        }
      } catch (error: any) {
        setLoadingGroupId(null)
        toast({
          title: translate("Error"),
          description: translate(error?.message),
          variant: "error",
        })
      }
    },
    [organization_id, filesLoaded],
  )

  const handleGetPinnedProjects = useCallback(
    async (forceRefetch: boolean = false) => {
      if (pinnedProjectsLoaded && !forceRefetch) {
        return
      }

      setLoadingGroupId(2) // 2 = Pinned Projects group_id

      try {
        const data = await getProjectListService({
          by_organization_id: organization_id,
          is_pinned: true,
        })

        if (data) {
          const pinnedItems = (data.items || []).map((project: any) => ({
            title: project?.title || "",
            path: `${PINNED_PROJECT_PAGE_ROUTE}/${project?.id}`,
            breadcrumbs: [
              { title: "Pinned Projects", path: PINNED_PROJECT_PAGE_ROUTE },
              { title: project?.title || "", path: `${PINNED_PROJECT_PAGE_ROUTE}/${project?.id}` },
            ],
          }))

          setAllNavbarItems((prev) =>
            prev.map((nav) => {
              if (nav.title === "Pinned Projects" && nav.group_id === 2)
                return { ...nav, children: pinnedItems }
              return nav
            }),
          )

          setPinnedProjectsLoaded(true)
          setLoadingGroupId(null)
        }
      } catch (error: any) {
        setLoadingGroupId(null)
        toast({
          title: translate("Error"),
          description: translate(error?.message),
          variant: "error",
        })
      }
    },
    [organization_id, pinnedProjectsLoaded],
  )

  // Toggle a top-level menu (Project / Files). Closing any other open menu is
  // automatic because `openMenu` is a single value. Data is lazy-loaded only on
  // the opening transition.
  const handleToggleMenu = useCallback(
    (menu: "project" | "files") => {
      const willOpen = openMenu !== menu
      setOpenMenu(willOpen ? menu : null)

      if (!willOpen) return

      if (menu === "project") {
        handleGetSelectedProjectGroup()
        handleGetPinnedProjects()
      } else if (menu === "files") {
        handleGetFolderList()
      }
    },
    [openMenu, handleGetSelectedProjectGroup, handleGetPinnedProjects, handleGetFolderList],
  )

  // Sync the open menu + lazy-loaded data with the current URL. Navigating to
  // /projects/* opens the Project menu; /files/* opens the Files menu; any
  // other route (Home, My Tasks, Users, Analytics, Financial, Pinned, etc.)
  // collapses both dropdowns so no stale item stays highlighted.
  useEffect(() => {
    const path = location.pathname

    const isProjectPath =
      path.startsWith(PROJECT_GROUP_PAGE_ROUTE) ||
      (path.startsWith(PROJECT_PAGE_ROUTE) && !path.startsWith(PINNED_PROJECT_PAGE_ROUTE))
    const isPinnedPath = path.startsWith(PINNED_PROJECT_PAGE_ROUTE)
    const isFilesPath = path.startsWith(FILES_FOLDER_PAGE_ROUTE)

    if (isProjectPath) setOpenMenu("project")
    else if (isFilesPath) setOpenMenu("files")
    else setOpenMenu(null)

    if (isProjectPath) {
      handleGetSelectedProjectGroup()
      handleGetPinnedProjects()
    }
    if (isPinnedPath) {
      handleGetPinnedProjects()
      handleGetSelectedProjectGroup()
    }
    if (isFilesPath) {
      handleGetFolderList()
    }
  }, [location.pathname])

  // Handle project updates
  useEffect(() => {
    if (projectUpdate) {
      setProjectsLoaded(false) // Force reload
      setPinnedProjectsLoaded(false) // Force reload pinned projects too
      handleGetSelectedProjectGroup()
      handleGetPinnedProjects(true)
      setProjectUpdateAction(false)
    }
  }, [projectUpdate])

  // Handle folder refetch
  useEffect(() => {
    if (isRefetchFolders) {
      handleGetFolderList(true) // Force refetch
      setIsRefetchFoldersAction(false)
    }
  }, [isRefetchFolders])

  function findProjectByPath(
    projectGroups: ProjectGroup[],
    targetPath: string,
  ): { node: ProjectGroup | null; level: number } {
    let level = 0

    function recursiveSearch(
      groups: ProjectGroup[],
      path: string,
      currentLevel: number,
    ): ProjectGroup | null {
      for (let group of groups) {
        // Check if the current group path matches
        if (group.path === path) {
          level = currentLevel
          return group
        }
        // Recursively search in children
        if ((group?.children ?? []).length > 0) {
          const result = recursiveSearch(group?.children ?? [], path, currentLevel + 1)
          if (result) return result
        }
      }
      return null // If path doesn't match any node
    }

    const foundNode = recursiveSearch(projectGroups, targetPath, 0)
    return foundNode ? { node: foundNode, level: level } : { node: null, level: -1 }
  }

  const buildNode = (
    group: Group,
    parentBreadcrumbs: Array<{ title: string; path: string }> = [],
  ): TreeNode => {
    const breadcrumbs = [
      ...parentBreadcrumbs,
      {
        title: group?.name || "",
        path: `${PROJECT_GROUP_PAGE_ROUTE}/${group?.id}`,
      },
    ]

    const children = group._sub_groups.map((subGroup) => buildNode(subGroup, breadcrumbs))

    const projectChildren = group._projects.map((project) => ({
      title: project?.title || "",
      path: `${PROJECT_PAGE_ROUTE}/${project?.id}`,
      breadcrumbs: [
        ...breadcrumbs,
        { title: project?.title || "", path: `${PROJECT_PAGE_ROUTE}/${project?.id}` },
      ],
    }))

    return {
      title: group?.name || "",
      path: `${PROJECT_GROUP_PAGE_ROUTE}/${group?.id}`,
      breadcrumbs: breadcrumbs,
      children: [...children, ...projectChildren],
    }
  }

  const handleClose = useCallback(() => {
    onClose()
  }, [])

  useEffect(() => {
    if (location.pathname) handleSetBreadcrumbs(allNavbarItems)
  }, [location.pathname, allNavbarItems])

  function buildFolderHierarchy(treeData: any, foldersData: any) {
    const folderDetailsMap = new Map()
    foldersData.forEach((folder: any) => folderDetailsMap.set(folder.id, folder))

    function _buildNode(currentTreeNodeId: any, currentTreeNodeValue: any) {
      const folderInfo = folderDetailsMap.get(currentTreeNodeId)

      if (!folderInfo) return null

      const node = { ...folderInfo }
      node.sub = []

      if (typeof currentTreeNodeValue === "object" && currentTreeNodeValue !== null) {
        for (const childIdStr in currentTreeNodeValue) {
          if (Object.prototype.hasOwnProperty.call(currentTreeNodeValue, childIdStr)) {
            try {
              const childId = parseInt(childIdStr, 10)
              const childValue = currentTreeNodeValue[childIdStr]
              const childBuiltNode = _buildNode(childId, childValue)
              if (childBuiltNode) node.sub.push(childBuiltNode)
            } catch (error) {
              // error handled silently
            }
          }
        }
      }

      return node
    }

    const resultTree = []

    // Basic validation for treeData input.
    if (typeof treeData !== "object" || treeData === null || Array.isArray(treeData)) {
      console.error("Error: treeData must be a non-null object.")
      return []
    }

    for (const rootIdStr in treeData) {
      if (Object.prototype.hasOwnProperty.call(treeData, rootIdStr)) {
        try {
          const rootId = parseInt(rootIdStr, 10) // Parse string ID to integer
          const rootValue = treeData[rootIdStr]

          // Build the node for each identified root folder.
          const builtRootNode = _buildNode(rootId, rootValue)

          // If the root node was successfully built, add it to the final result.
          if (builtRootNode) {
            resultTree.push(builtRootNode)
          }
        } catch (error) {
          // error handled silently
        }
      }
    }

    return resultTree
  }

  function buildFolderTreeWithBreadcrumbs(
    folders: any[],
    parentBreadcrumbs: any[] = [],
  ): FolderTreeResult[] {
    // Add the root "Files" breadcrumb if not already present
    const baseBreadcrumbs =
      parentBreadcrumbs.length === 0
        ? [{ title: "Files", path: FILES_FOLDER_PAGE_ROUTE }]
        : parentBreadcrumbs

    return folders.map((folder) => {
      const path = `${FILES_FOLDER_PAGE_ROUTE}/${folder.id}`

      const currentBreadcrumb: Breadcrumb = {
        title: folder.name,
        path,
      }

      const breadcrumbs = [...baseBreadcrumbs, currentBreadcrumb]

      const hasChildren = folder.sub && folder.sub.length > 0

      const node: FolderTreeResult = {
        title: folder.name,
        path,
        breadcrumbs,
      }

      // 🔁 Only add children if sub has elements
      if (hasChildren) {
        node.children = buildFolderTreeWithBreadcrumbs(folder.sub!, breadcrumbs)
      }

      return node
    })
  }

  return (
    <>
      {isOpen && (
        <div className='fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden' onClick={onClose} />
      )}

      <div
        className={cn(
          "hidden h-screen w-64 flex-col bg-indigo-25 transition-transform duration-300 ease-in-out lg:flex",
          "fixed z-50 lg:static",
          isHebrew
            ? isOpen
              ? "translate-x-0"
              : "translate-x-full lg:translate-x-0"
            : isOpen
              ? "translate-x-0"
              : "-translate-x-full lg:translate-x-0",
          className,
        )}
      >
        {/* Header */}
        <NavbarHeader onClose={handleClose} />

        {/* Navigation Items */}
        <div className='flex-1 overflow-y-auto'>
          <NavBarItems
            items={allNavbarItems}
            loadingGroupId={loadingGroupId}
            openMenu={openMenu}
            onToggleMenu={handleToggleMenu}
          />
        </div>

        {/* Bottom Actions */}
        <NavbarBottom />
      </div>
    </>
  )
}

export default memo(Sidebar)
